import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  billingService,
  GenerateInvoicePayload,
  InvoiceStatus,
} from '../services/billingService';
import { queryKeys } from '../lib/queryClient';

export const useInvoices = (clientId?: number) =>
  useQuery({
    queryKey: queryKeys.billing.invoices(clientId),
    queryFn: () => billingService.getInvoices(clientId),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

export const useInvoice = (id: number | null) =>
  useQuery({
    queryKey: queryKeys.billing.invoice(id!),
    queryFn: () => billingService.getInvoice(id!),
    enabled: id != null,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

export const useGenerateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: GenerateInvoicePayload) =>
      billingService.generateInvoice(payload),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.invoices() });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.invoices(invoice.clientId) });
    },
  });
};

export const useUpdateInvoiceStatus = (invoiceId: number, clientId?: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: InvoiceStatus) =>
      billingService.updateStatus(invoiceId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.invoice(invoiceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.invoices(clientId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.invoices() });
    },
  });
};

export const useUpdateInvoiceEinvoice = (invoiceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { irnNo?: string; ackNo?: string; ackDate?: string }) =>
      billingService.updateEinvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.invoice(invoiceId) });
    },
  });
};
